import { AdminLayout } from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AdminPageTitle } from "@/project/jain_elibrary-admin/components/shared/AdminPageTitle";
import { AdminPaginationBar } from "@/project/jain_elibrary-admin/components/shared/AdminPaginationBar";
import { AdminEmptyState } from "@/project/jain_elibrary-admin/components/shared/AdminEmptyState";
import { AdminTableSkeleton } from "@/project/jain_elibrary-admin/components/shared/AdminTableSkeleton";
import {
  Search,
  Loader2,
  Download,
  Eye,
  Calendar,
  CreditCard,
  MapPin,
  ClipboardList,
  ExternalLink,
  Clock,
  Package,
  User as UserIcon,
  Inbox,
  CheckCircle,
} from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import { adminService, ShoppingOrder } from "../../services/adminService";
import { toast } from "sonner";
import { showErrorToast } from "@/utils/errorHandler";
import * as XLSX from "xlsx";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";

const OrderManagement = () => {
  const [orders, setOrders] = useState<ShoppingOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedOrder, setSelectedOrder] = useState<ShoppingOrder | null>(
    null
  );
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [markingId, setMarkingId] = useState<number | null>(null);
  const [pagination, setPagination] = useState({
    count: 0,
    page: 1,
    pageSize: 10,
  });

  // Offline / India orders are placed with payment pending; an admin confirms
  // receipt here. PayPal orders are captured automatically, so they're excluded.
  const canMarkPaid = (order: ShoppingOrder) =>
    !order.payment_received &&
    order.payment_method?.toLowerCase() !== "paypal" &&
    order.status?.toLowerCase() !== "cancelled";

  const handleMarkPaymentReceived = async (order: ShoppingOrder) => {
    try {
      setMarkingId(order.id);
      const res = await adminService.markShoppingOrderPaymentReceived(order.id);
      toast.success(res.message || "Payment marked as received.");
      setOrders(prev =>
        prev.map(o =>
          o.id === order.id
            ? { ...o, payment_received: true, status: res.status ?? o.status }
            : o
        )
      );
      setSelectedOrder(prev =>
        prev?.id === order.id
          ? {
              ...prev,
              payment_received: true,
              status: res.status ?? prev.status,
            }
          : prev
      );
    } catch (error: any) {
      showErrorToast(error, "Failed to mark payment as received.");
    } finally {
      setMarkingId(null);
    }
  };

  const fetchOrders = useCallback(
    async (page = 1, search = "") => {
      try {
        setLoading(true);
        const data = await adminService.getShoppingOrders({
          page: page,
          page_size: pagination.pageSize,
          search: search,
          ordering: "-created_at",
        });
        setOrders(data?.results || []);
        setPagination(prev => ({
          ...prev,
          count: data?.count || 0,
          page: page,
        }));
      } catch (error: any) {
        console.error("Error fetching orders:", error);
        showErrorToast(error, "Failed to load orders. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [pagination.pageSize]
  );

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchOrders(1, searchTerm);
  };

  const handlePageSizeChange = (nextPageSize: number) => {
    setPagination(prev => ({ ...prev, pageSize: nextPageSize, page: 1 }));
  };

  const handleViewDetails = (order: ShoppingOrder) => {
    setSelectedOrder(order);
    setIsDetailModalOpen(true);
  };

  const handleExport = () => {
    if (orders.length === 0) {
      toast.error("No orders to export.");
      return;
    }

    const exportData = orders.flatMap(order =>
      order.items.map(item => ({
        ID: order.id,
        "Order Number": order.order_number,
        Organization: order.shipping_address?.organization ?? "N/A",
        "Item ID": item.sku ?? item.product_id ?? "N/A",
        "Item Title": item.title ?? "N/A",
        Quantity: item.quantity,
        Price: item.price,
        Subtotal: order.subtotal,
        "Shipping Cost": order.shipping_cost,
        "Donation Amount": order.donation_amount,
        "Total Amount": order.total,
        "Payment Method": order.payment_method,
        Source: order.source,
        "Customer Name":
          order.shipping_full_name ?? order.shipping_address?.fullName ?? "N/A",
        "Customer Email":
          order.shipping_email ?? order.shipping_address?.email ?? "N/A",
        Status: order.status,
        "Order Date": new Date(order.created_at).toLocaleString(),
      }))
    );

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Orders");
    XLSX.writeFile(
      workbook,
      `Advanced_Order_Export_${new Date().toISOString().split("T")[0]}.xlsx`
    );
    toast.success("Advanced order data exported!");
  };

  const totalPages = Math.ceil(pagination.count / pagination.pageSize);

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "delivered":
        return "bg-green-100 text-green-800 border-green-200";
      case "pending":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "cancelled":
        return "bg-red-100 text-red-800 border-red-200";
      case "processing":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "shipped":
        return "bg-purple-100 text-purple-800 border-purple-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <AdminPageTitle
          title="Order management"
          description="View orders across the storefront. Read-only here—edit line items from Purchase orders when needed."
          actions={
            <Button
              type="button"
              onClick={handleExport}
              className="flex items-center gap-2 bg-[#654321] text-white hover:bg-[#543210]"
            >
              <Download className="h-4 w-4" aria-hidden />
              Export full data
            </Button>
          }
        />

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <div className="rounded-lg border border-border bg-card p-4 shadow-sm">
            <div className="mb-1 text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Total Orders
            </div>
            <div className="font-mono text-2xl font-bold text-green-600">
              {pagination.count}
            </div>
          </div>
          <div className="rounded-lg border border-border bg-card p-4 shadow-sm">
            <div className="mb-1 text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Active Pages
            </div>
            <div className="font-mono text-2xl font-bold text-green-600">
              {String(totalPages).padStart(2, "0")}
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="rounded-lg border border-border bg-card p-4 shadow-sm">
          <div className="mb-2 text-sm font-medium">Search Orders</div>
          <form
            onSubmit={handleSearch}
            className="flex flex-col items-center gap-2 md:flex-row"
          >
            <div className="relative w-full max-w-sm">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search by Order Number or Name..."
                className="w-full pl-9"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="flex w-full items-center gap-2 md:w-auto">
              <Button
                type="submit"
                className="w-full bg-[#654321] px-6 text-white hover:bg-[#543210] md:w-auto"
              >
                Search
              </Button>
              <Button
                type="button"
                variant="outline"
                className="w-full md:w-auto"
                onClick={() => {
                  setSearchTerm("");
                  fetchOrders(1, "");
                }}
              >
                Clear
              </Button>
            </div>
          </form>
        </div>

        <div className="overflow-hidden rounded-lg border border-border bg-card shadow-sm">
          {loading && orders.length === 0 ? (
            <AdminTableSkeleton rows={10} columns={9} className="border-0" />
          ) : null}
          {!(loading && orders.length === 0) ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-muted/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      ID
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Order #
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Customer
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Source
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Payment
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Total
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Status
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {loading && orders.length > 0 ? (
                    <tr>
                      <td
                        colSpan={9}
                        className="border-t border-border bg-muted/20 px-4 py-2 text-center text-xs text-muted-foreground"
                      >
                        <span className="inline-flex items-center justify-center gap-2">
                          <Loader2
                            className="h-4 w-4 animate-spin"
                            aria-hidden
                          />
                          Updating results…
                        </span>
                      </td>
                    </tr>
                  ) : null}
                  {!loading && orders.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="p-6">
                        <AdminEmptyState
                          icon={Inbox}
                          title="No orders match your filters"
                          description="Try clearing search or check another page. New orders appear here after checkout."
                        />
                      </td>
                    </tr>
                  ) : null}
                  {orders.length > 0
                    ? orders.map(order => (
                        <tr
                          key={order.id}
                          className="group transition-colors hover:bg-muted/30"
                        >
                          <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                            #{order.id}
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 text-xs">
                            {new Date(order.created_at).toLocaleDateString()}
                          </td>
                          <td
                            className="max-w-[150px] truncate px-4 py-3 text-xs font-medium"
                            title={order.order_number}
                          >
                            {order.order_number}
                          </td>
                          <td className="px-4 py-3 text-xs">
                            <div className="font-medium">
                              {order.shipping_full_name}
                            </div>
                            <div className="max-w-[120px] truncate text-[10px] text-muted-foreground">
                              {order.shipping_address?.organization}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-xs">
                            <Badge
                              variant="outline"
                              className="text-[10px] font-normal capitalize"
                            >
                              {order.source?.replace("-", " ")}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-xs">
                            <div className="capitalize">
                              {order.payment_method?.replace("_", " ")}
                            </div>
                            <span
                              className={`mt-1 inline-flex items-center rounded-full border px-2 py-0.5 text-[9px] font-bold uppercase tracking-tighter ${
                                order.payment_received
                                  ? "border-green-200 bg-green-100 text-green-800"
                                  : "border-orange-200 bg-orange-100 text-orange-800"
                              }`}
                            >
                              {order.payment_received ? "Paid" : "Unpaid"}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right font-mono font-medium">
                            ${parseFloat(order.total.toString()).toFixed(2)}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span
                              className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-tighter ${getStatusColor(order.status)}`}
                            >
                              {order.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex items-center justify-center gap-1">
                              {canMarkPaid(order) && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-green-700 hover:bg-green-600 hover:text-white"
                                  onClick={() =>
                                    handleMarkPaymentReceived(order)
                                  }
                                  disabled={markingId === order.id}
                                  title="Mark payment received"
                                >
                                  {markingId === order.id ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <CheckCircle className="h-4 w-4" />
                                  )}
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 hover:bg-[#654321] hover:text-white"
                                onClick={() => handleViewDetails(order)}
                                title="View Details"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    : null}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>

        <AdminPaginationBar
          page={pagination.page}
          pageSize={pagination.pageSize}
          totalCount={pagination.count}
          loading={loading}
          onPageChange={p => void fetchOrders(p, searchTerm)}
          onPageSizeChange={handlePageSizeChange}
        />
      </div>

      {/* Detail Modal */}
      <Dialog open={isDetailModalOpen} onOpenChange={setIsDetailModalOpen}>
        <DialogContent className="max-h-[90vh] max-w-4xl gap-0 overflow-y-auto p-0">
          <DialogHeader className="sticky top-0 z-10 border-b bg-muted/30 p-6 backdrop-blur-sm">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#654321]/10 text-[#654321]">
                <Package className="h-6 w-6" />
              </div>
              <div className="text-left">
                <DialogTitle className="text-2xl font-bold">
                  Order Details (View only)
                </DialogTitle>
                <DialogDescription className="font-mono text-xs">
                  Internal ID: {selectedOrder?.id} | Transaction ID:{" "}
                  {selectedOrder?.order_number}
                </DialogDescription>
              </div>
              <div className="ml-auto">
                <Badge
                  className={
                    getStatusColor(selectedOrder?.status ?? "") +
                    " px-3 py-1 text-xs uppercase"
                  }
                >
                  {selectedOrder?.status}
                </Badge>
              </div>
            </div>
          </DialogHeader>

          <div className="space-y-8 p-6">
            {/* Grid Layout for all fields */}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              {/* Column 1: Core Info */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 border-b pb-1 text-sm font-bold text-[#654321]">
                  <ClipboardList className="h-4 w-4" />
                  General Information
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-[10px] font-bold uppercase text-muted-foreground">
                      Order Number
                    </span>
                    <span className="font-mono text-xs">
                      {selectedOrder?.order_number}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[10px] font-bold uppercase text-muted-foreground">
                      Source Branch
                    </span>
                    <span className="capitalize">
                      {selectedOrder?.source?.replace("-", " ")}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[10px] font-bold uppercase text-muted-foreground">
                      Created Date
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {selectedOrder?.created_at &&
                        new Date(selectedOrder.created_at).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[10px] font-bold uppercase text-muted-foreground">
                      Last Updated
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {selectedOrder?.updated_at &&
                        new Date(selectedOrder.updated_at).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[10px] font-bold uppercase text-muted-foreground">
                      Internal DB ID
                    </span>
                    <span className="font-mono text-xs">
                      {selectedOrder?.id}
                    </span>
                  </div>
                  <div className="mt-2 flex justify-between border-t pt-2">
                    <span className="text-[10px] font-bold uppercase text-muted-foreground">
                      Associated CRM User ID
                    </span>
                    <span className="font-mono text-xs">
                      {selectedOrder?.user?.id ?? "Public/Guest"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Column 2: Financial & Payment */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 border-b pb-1 text-sm font-bold text-[#654321]">
                  <CreditCard className="h-4 w-4" />
                  Financial Records
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-[10px] font-bold uppercase text-muted-foreground">
                      Subtotal Amount
                    </span>
                    <span className="font-mono font-medium">
                      $
                      {parseFloat(
                        selectedOrder?.subtotal?.toString() ?? "0"
                      ).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[10px] font-bold uppercase text-muted-foreground">
                      Shipping Charges
                    </span>
                    <span className="font-mono font-medium">
                      $
                      {parseFloat(
                        selectedOrder?.shipping_cost?.toString() ?? "0"
                      ).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between text-green-600">
                    <span className="text-[10px] font-bold uppercase">
                      Donations Included
                    </span>
                    <span className="font-mono font-medium">
                      +$
                      {parseFloat(
                        selectedOrder?.donation_amount?.toString() ?? "0"
                      ).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between border-t border-[#654321]/20 pt-2 font-black italic text-[#654321]">
                    <span className="text-xs font-bold uppercase">
                      Grand Total
                    </span>
                    <span className="font-mono text-lg">
                      $
                      {parseFloat(
                        selectedOrder?.total?.toString() ?? "0"
                      ).toFixed(2)}
                    </span>
                  </div>
                  <div className="mt-4 space-y-2 border-t pt-4">
                    <div className="flex justify-between">
                      <span className="text-[10px] font-bold uppercase text-muted-foreground">
                        Payment Method
                      </span>
                      <span className="capitalize">
                        {selectedOrder?.payment_method?.replace("_", " ")}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-bold uppercase text-muted-foreground">
                        Payment Status
                      </span>
                      <span
                        className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase ${
                          selectedOrder?.payment_received
                            ? "border-green-200 bg-green-100 text-green-800"
                            : "border-orange-200 bg-orange-100 text-orange-800"
                        }`}
                      >
                        {selectedOrder?.payment_received ? "Paid" : "Unpaid"}
                      </span>
                    </div>
                    {selectedOrder?.paypal_order_id && (
                      <div className="flex justify-between">
                        <span className="text-[10px] font-bold uppercase text-muted-foreground">
                          PayPal ID
                        </span>
                        <span className="max-w-[120px] truncate font-mono text-[10px]">
                          {selectedOrder.paypal_order_id}
                        </span>
                      </div>
                    )}
                    {selectedOrder?.paypal_transaction_id && (
                      <div className="flex justify-between">
                        <span className="text-[10px] font-bold uppercase text-muted-foreground">
                          TXN ID
                        </span>
                        <span className="max-w-[120px] truncate font-mono text-[10px]">
                          {selectedOrder.paypal_transaction_id}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Column 3: Customer & Shipping */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 border-b pb-1 text-sm font-bold text-[#654321]">
                  <MapPin className="h-4 w-4" />
                  Customer & Delivery
                </div>
                <div className="space-y-2 rounded border border-dashed border-border bg-muted/20 p-3 text-sm">
                  <div className="flex items-center gap-2 font-bold">
                    <UserIcon className="h-3 w-3" />
                    {selectedOrder?.shipping_full_name ??
                      selectedOrder?.shipping_address?.fullName}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {selectedOrder?.shipping_email ??
                      selectedOrder?.shipping_address?.email}
                  </div>
                  <div className="pt-1 text-xs">
                    {selectedOrder?.shipping_address?.organization && (
                      <div className="font-medium text-[#654321]">
                        {selectedOrder.shipping_address.organization}
                      </div>
                    )}
                    <div className="mt-1">
                      {selectedOrder?.shipping_address?.addressLine1}
                      <br />
                      {selectedOrder?.shipping_address?.addressLine2 && (
                        <>
                          {selectedOrder.shipping_address.addressLine2}
                          <br />
                        </>
                      )}
                      {selectedOrder?.shipping_address?.city},{" "}
                      {selectedOrder?.shipping_address?.state}{" "}
                      {selectedOrder?.shipping_address?.postalCode}
                      <br />
                      <span className="text-[10px] font-bold uppercase tracking-widest">
                        {selectedOrder?.shipping_address?.country}
                      </span>
                    </div>
                    <div className="mt-2 inline-block rounded border bg-white/50 px-2 py-1 text-[10px] font-medium">
                      Phone: {selectedOrder?.shipping_address?.phone ?? "N/A"}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Proof Section - If India order */}
            {(selectedOrder?.payment_proof_url ??
              selectedOrder?.payment_proof_notes) && (
              <div className="space-y-2 rounded-lg border border-yellow-200 bg-yellow-50 p-4">
                <div className="text-xs font-bold uppercase tracking-wider text-yellow-800">
                  Payment Verification Documents
                </div>
                <div className="flex flex-col gap-4 md:flex-row">
                  {selectedOrder?.payment_proof_url && (
                    <a
                      href={selectedOrder.payment_proof_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 rounded border bg-white px-3 py-2 text-sm font-medium text-blue-600 shadow-sm hover:underline"
                    >
                      <ExternalLink className="h-4 w-4" />
                      View Uploaded Payment Proof
                    </a>
                  )}
                  {selectedOrder?.payment_proof_notes && (
                    <div className="flex-1 rounded border bg-white p-3 text-xs text-yellow-900 shadow-sm">
                      <span className="mb-1 block font-bold">
                        Administrative Notes:
                      </span>
                      {selectedOrder.payment_proof_notes}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Delivery info (read-only) */}
            {(selectedOrder?.courier_name ??
              selectedOrder?.tracking_number ??
              selectedOrder?.date_shipped) && (
              <div className="space-y-2 rounded-lg border border-border bg-muted/20 p-4">
                <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Delivery Information
                </div>
                <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
                  {selectedOrder?.courier_name && (
                    <span>Courier: {selectedOrder.courier_name}</span>
                  )}
                  {selectedOrder?.tracking_number && (
                    <span>Tracking: {selectedOrder.tracking_number}</span>
                  )}
                  {selectedOrder?.date_shipped && (
                    <span>
                      Shipped:{" "}
                      {new Date(
                        selectedOrder.date_shipped
                      ).toLocaleDateString()}
                    </span>
                  )}
                  {selectedOrder?.payment_received !== null &&
                    selectedOrder?.payment_received !== undefined && (
                      <span>
                        Payment received:{" "}
                        {selectedOrder.payment_received ? "Yes" : "No"}
                      </span>
                    )}
                  {selectedOrder?.date_received && (
                    <span>
                      Received:{" "}
                      {new Date(
                        selectedOrder.date_received
                      ).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Items Table */}
            <div className="space-y-4">
              <div className="flex items-center justify-between border-b pb-1 text-sm font-bold text-[#654321]">
                <div className="flex items-center gap-2">
                  <Package className="h-4 w-4" />
                  Order Manifest ({selectedOrder?.items?.length} Items)
                </div>
              </div>
              <div className="overflow-hidden rounded-md border bg-white shadow-sm">
                <table className="w-full text-sm">
                  <thead className="bg-[#654321]/5 text-[#654321]">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-bold uppercase">
                        SKU / Item ID
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-bold uppercase">
                        Product Title
                      </th>
                      <th className="px-4 py-2 text-center text-xs font-bold uppercase">
                        Price
                      </th>
                      <th className="px-4 py-2 text-center text-xs font-bold uppercase">
                        Qty
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-bold uppercase">
                        Subtotal
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {selectedOrder?.items?.map((item, idx) => (
                      <tr
                        key={item.id ?? `${item.product_id}-${idx}`}
                        className="transition-colors hover:bg-muted/5"
                      >
                        <td className="px-4 py-3 font-mono text-xs font-bold">
                          {item.sku ?? item.product_id}
                        </td>
                        <td className="px-4 py-3 text-xs">{item.title}</td>
                        <td className="px-4 py-3 text-center font-mono text-xs">
                          ${parseFloat(item.price ?? "0").toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-center font-bold text-[#654321]">
                          {item.quantity}
                        </td>
                        <td className="px-4 py-3 text-right font-mono font-medium">
                          $
                          {(
                            parseFloat(item.price ?? "0") * (item.quantity ?? 1)
                          ).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="border-t-2 border-[#654321]/10 bg-muted/10 font-bold">
                    <tr>
                      <td
                        colSpan={4}
                        className="px-4 py-2 text-right text-[10px] uppercase"
                      >
                        Manifest Subtotal
                      </td>
                      <td className="px-4 py-2 text-right font-mono">
                        $
                        {parseFloat(
                          (selectedOrder?.subtotal ?? 0).toString()
                        ).toFixed(2)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 border-t bg-muted/10 p-6">
            {selectedOrder && canMarkPaid(selectedOrder) && (
              <Button
                onClick={() => handleMarkPaymentReceived(selectedOrder)}
                disabled={markingId === selectedOrder.id}
                className="bg-green-700 text-white hover:bg-green-800"
              >
                {markingId === selectedOrder.id ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <CheckCircle className="mr-2 h-4 w-4" />
                )}
                Mark Payment Received
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => setIsDetailModalOpen(false)}
            >
              Close Order Detail View
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default OrderManagement;
